/* eslint-disable react-refresh/only-export-components -- hook exported alongside modal component */
import { useRegisterSW } from 'virtual:pwa-register/react';
import { useEffect, useState, useCallback, useRef } from 'react';

const LAST_UPDATE_CHECK_KEY = 'last-update-check';
const UPDATE_CHECK_INTERVAL = 5 * 60 * 1000; // Ask the browser to re-fetch sw.js at most every 5 minutes
/** If the new worker has not taken control this long after SKIP_WAITING, fall back to hardReload(). */
const CONTROLLER_CHANGE_TIMEOUT = 4000;
/** hardReload() reloads no later than this, whatever CacheStorage or the registration are doing. */
const HARD_RELOAD_DEADLINE = 10000;

/**
 * Detect if running on iOS
 */
function isIOS(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.userAgent.includes('Mac') && navigator.maxTouchPoints > 1);
}

/**
 * Detect if running as installed PWA (standalone mode)
 */
function isStandalone(): boolean {
  return window.matchMedia('(display-mode: standalone)').matches ||
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window.navigator as any).standalone === true;
}

/**
 * Check if enough time has passed since last update check
 */
function shouldCheckForUpdate(): boolean {
  const lastCheck = localStorage.getItem(LAST_UPDATE_CHECK_KEY);
  if (!lastCheck) return true;
  const elapsed = Date.now() - parseInt(lastCheck, 10);
  return elapsed > UPDATE_CHECK_INTERVAL;
}

/**
 * Mark that we just checked for updates
 */
function markUpdateChecked(): void {
  localStorage.setItem(LAST_UPDATE_CHECK_KEY, Date.now().toString());
}

async function getRegistration(): Promise<ServiceWorkerRegistration | undefined> {
  if (!navigator.serviceWorker) return undefined;
  try {
    return await navigator.serviceWorker.getRegistration();
  } catch {
    return undefined;
  }
}

/**
 * Deletes the Workbox precache so that the next navigation is answered by the
 * network instead of the worker's precached index.html.
 */
async function clearCaches(): Promise<void> {
  if (!('caches' in window)) return;
  const keys = await caches.keys();
  await Promise.all(keys.map(key => caches.delete(key)));
}

async function unregisterServiceWorkers(): Promise<void> {
  if (!navigator.serviceWorker) return;
  const registrations = await navigator.serviceWorker.getRegistrations();
  await Promise.all(registrations.map(registration => registration.unregister()));
}

/**
 * Last resort: drop the precache and the registration, then reload from the
 * network. Only for a page that is already broken (a chunk failed to load) or
 * for a worker that ignored SKIP_WAITING. It throws away the new worker's
 * precache too, so the normal update path never calls it first.
 *
 * The reload waits for the async chain but is capped by a generous deadline,
 * so a stalled CacheStorage or registration promise can never leave the button
 * dead.
 */
export function hardReload(): void {
  let reloaded = false;
  const reload = () => {
    if (reloaded) return;
    reloaded = true;
    window.location.reload();
  };

  setTimeout(reload, HARD_RELOAD_DEADLINE);

  void clearCaches()
    .then(unregisterServiceWorkers)
    .catch(() => {})
    .finally(reload);
}

/**
 * Hook that shows a centered modal when a new version of the app is installed
 * and waiting. Returns the modal state and the refresh handler.
 *
 * The only update signal is "a new service worker is waiting". Refresh posts
 * SKIP_WAITING to it and reloads once it has taken control, so the new build is
 * served straight from the precache it already downloaded — nothing is deleted
 * or re-downloaded.
 *
 * iOS does not check for service worker updates when a home-screen PWA is
 * resumed from the background, so on iOS standalone we ask for one ourselves.
 */
export function usePWAUpdate() {
  const [updateFound, setUpdateFound] = useState(false);
  const regRef = useRef<ServiceWorkerRegistration | null>(null);

  const { needRefresh: [needRefresh, setNeedRefresh], updateServiceWorker } = useRegisterSW({
    onRegisteredSW(_swUrl, registration) {
      regRef.current = registration ?? null;
    },
  });

  // onRegisteredSW fires asynchronously after register() resolves, so fall
  // back to asking the browser while the ref is still empty.
  const resolveRegistration = useCallback(
    async () => regRef.current ?? (await getRegistration()) ?? null,
    [],
  );

  // iOS resume: report a worker that is already waiting (cheap, local, every
  // tick), and at most every 5 minutes ask the browser to re-fetch sw.js.
  // registration.update() is fire-and-forget: it only settles once the whole
  // install has finished.
  const checkForUpdates = useCallback(async () => {
    const registration = await resolveRegistration();
    if (!registration) return;
    if (registration.waiting) {
      setUpdateFound(true);
      return;
    }
    if (!shouldCheckForUpdate()) return;
    markUpdateChecked();
    void registration.update().catch(() => {});
  }, [resolveRegistration]);

  const showModal = needRefresh || updateFound;

  // Refresh: activate the waiting worker and reload once it controls the page.
  const applyUpdate = useCallback(() => {
    void resolveRegistration().then(registration => {
      if (!registration?.waiting) {
        // Nothing to activate: the worker went redundant (iOS suspended the app
        // mid-install) or the prompt fired a moment before `waiting`. Ask for a
        // fresh update and hide the modal; it comes back once a worker really
        // is waiting. Reloading here would be answered by the old worker's
        // precached index.html and put the user straight back in the modal.
        void registration?.update().catch(() => {});
        setNeedRefresh(false);
        setUpdateFound(false);
        return;
      }

      let done = false;
      const onControllerChange = () => {
        if (done) return;
        done = true;
        clearTimeout(fallback);
        window.location.reload();
      };
      // If the worker never takes control, escalate to the destructive path —
      // never a plain reload, which the old worker would answer from precache.
      const fallback = setTimeout(() => {
        if (done) return;
        done = true;
        navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
        hardReload();
      }, CONTROLLER_CHANGE_TIMEOUT);

      navigator.serviceWorker.addEventListener('controllerchange', onControllerChange, { once: true });
      void updateServiceWorker(true);
    });
  }, [resolveRegistration, setNeedRefresh, updateServiceWorker]);

  // iOS-specific: Check for updates on app resume/visibility change
  useEffect(() => {
    // Only apply iOS workaround for standalone PWA mode
    if (!isIOS() || !isStandalone()) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void checkForUpdates();
      }
    };

    const handlePageShow = (event: PageTransitionEvent) => {
      // pageshow fires when navigating back to the page (bfcache)
      if (event.persisted) {
        void checkForUpdates();
      }
    };

    const handleFocus = () => {
      void checkForUpdates();
    };

    // Check on mount for iOS PWA (deferred to avoid cascading renders)
    const timeoutId = setTimeout(() => void checkForUpdates(), 100);

    // Listen for various resume events
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pageshow', handlePageShow);
    window.addEventListener('focus', handleFocus);

    // Also check periodically while app is open (iOS can keep PWA alive long)
    const intervalId = setInterval(() => {
      if (document.visibilityState === 'visible') {
        void checkForUpdates();
      }
    }, UPDATE_CHECK_INTERVAL);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('focus', handleFocus);
      clearTimeout(timeoutId);
      clearInterval(intervalId);
    };
  }, [checkForUpdates]);

  return { showModal, handleRefresh: applyUpdate };
}

/**
 * Centered PWA Update Modal Component
 * Responsive for both mobile and desktop screens
 */
export function PWAUpdateModal({
  show,
  onRefresh
}: {
  show: boolean;
  onRefresh: () => void;
}) {
  // Unmounting the content resets its "Updating…" state, so the button is live
  // again if the hook withdraws the modal and later brings it back.
  return show ? <PWAUpdateModalContent onRefresh={onRefresh} /> : null;
}

function PWAUpdateModalContent({ onRefresh }: { onRefresh: () => void }) {
  const [updating, setUpdating] = useState(false);

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.6)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        padding: '20px',
      }}
    >
      <div
        style={{
          background: 'var(--color-bg-secondary)',
          borderRadius: '16px',
          padding: '24px',
          maxWidth: '320px',
          width: '100%',
          textAlign: 'center',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
          border: '1px solid var(--color-border)',
          animation: 'modalFadeIn 0.2s ease-out',
        }}
      >
        {/* Update icon */}
        <div
          style={{
            width: '56px',
            height: '56px',
            borderRadius: '50%',
            background: 'var(--color-info)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
            fontSize: '28px',
          }}
        >
          🚀
        </div>

        {/* Title */}
        <h3
          style={{
            margin: '0 0 8px',
            fontSize: '18px',
            fontWeight: 600,
            color: 'var(--color-text-primary)',
          }}
        >
          Update Available
        </h3>

        {/* Description */}
        <p
          style={{
            margin: '0 0 24px',
            fontSize: '14px',
            color: 'var(--color-text-secondary)',
            lineHeight: 1.5,
          }}
        >
          A new version of GPC Shifts is ready. Refresh to get the latest features and improvements.
        </p>

        {/* Refresh button */}
        <button
          onClick={() => {
            setUpdating(true);
            onRefresh();
          }}
          disabled={updating}
          style={{
            width: '100%',
            padding: '14px 24px',
            background: 'var(--color-info)',
            color: 'white',
            border: 'none',
            borderRadius: '12px',
            fontSize: '16px',
            fontWeight: 600,
            cursor: updating ? 'default' : 'pointer',
            opacity: updating ? 0.7 : 1,
            transition: 'transform 0.15s, box-shadow 0.15s',
            boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
          }}
          onMouseOver={(e) => {
            if (updating) return;
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 16px rgba(59, 130, 246, 0.4)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
          }}
        >
          {updating ? 'Updating…' : 'Refresh Now'}
        </button>
      </div>

      {/* Animation keyframes */}
      <style>{`
        @keyframes modalFadeIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
      `}</style>
    </div>
  );
}
